import 'leaflet/dist/leaflet.css';
import './availabilitiesMap.css';

import clsx from 'clsx';
import * as L from 'leaflet';
import { useEffect, useMemo, useRef, type JSX } from 'react';

export interface AvailabilitiesMapPoint {
  id: string;
  lat: number;
  lng: number;
  label: string;
  unitCount: number;
  emphasis?: boolean;
}

interface AvailabilitiesMapProps {
  points: AvailabilitiesMapPoint[];
  selectedPointId: string | null;
  focusRequest?: number;
  onSelectPoint: (pointId: string) => void;
  onUserInteraction?: () => void;
  className?: string;
}

const DEFAULT_CENTER = { lat: 43.6532, lng: -79.3832 };
const DEFAULT_ZOOM = 11;
const FOCUS_ZOOM = 14;

export function AvailabilitiesMap({
  points,
  selectedPointId,
  focusRequest = 0,
  onSelectPoint,
  onUserInteraction,
  className,
}: AvailabilitiesMapProps): JSX.Element {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerLayerRef = useRef<L.LayerGroup | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const hasFitBoundsRef = useRef(false);
  const onSelectPointRef = useRef(onSelectPoint);
  const onUserInteractionRef = useRef(onUserInteraction);

  useEffect(() => {
    onSelectPointRef.current = onSelectPoint;
  }, [onSelectPoint]);

  useEffect(() => {
    onUserInteractionRef.current = onUserInteraction;
  }, [onUserInteraction]);

  const pointsById = useMemo(() => new Map(points.map((point) => [point.id, point])), [points]);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || mapRef.current) return;

    const markers = markersRef.current;
    const map = L.map(container, {
      zoomControl: true,
      scrollWheelZoom: true,
      touchZoom: true,
      attributionControl: true,
      worldCopyJump: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    const markerLayer = L.layerGroup().addTo(map);

    mapRef.current = map;
    markerLayerRef.current = markerLayer;

    map.setView(L.latLng(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng), DEFAULT_ZOOM);

    return () => {
      markerLayer.clearLayers();
      map.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
      hasFitBoundsRef.current = false;

      markers.clear();
    };
  }, []);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) {
      return;
    }

    let interactionActive = false;
    let dragTriggered = false;
    let startX = 0;
    let startY = 0;
    const captureOptions: AddEventListenerOptions = { capture: true };
    const passiveCaptureOptions: AddEventListenerOptions = { capture: true, passive: true };

    const handleUserInteraction = () => {
      const handler = onUserInteractionRef.current;
      if (!handler) {
        return;
      }
      handler();
    };
    const startInteraction = (clientX: number, clientY: number) => {
      interactionActive = true;
      dragTriggered = false;
      startX = clientX;
      startY = clientY;
    };
    const moveInteraction = (clientX: number, clientY: number) => {
      if (!interactionActive || dragTriggered) {
        return;
      }
      const dx = Math.abs(clientX - startX);
      const dy = Math.abs(clientY - startY);
      if (dx + dy < 6) {
        return;
      }
      dragTriggered = true;
      handleUserInteraction();
    };
    const handleMouseMove = (event: MouseEvent) => {
      moveInteraction(event.clientX, event.clientY);
    };
    const handleMouseUp = () => {
      interactionActive = false;
      dragTriggered = false;
      window.removeEventListener('mousemove', handleMouseMove, captureOptions);
      window.removeEventListener('mouseup', handleMouseUp, captureOptions);
    };
    const handleMouseDown = (event: MouseEvent) => {
      startInteraction(event.clientX, event.clientY);
      window.addEventListener('mousemove', handleMouseMove, captureOptions);
      window.addEventListener('mouseup', handleMouseUp, captureOptions);
    };
    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      moveInteraction(touch.clientX, touch.clientY);
    };
    const handleTouchEnd = () => {
      interactionActive = false;
      dragTriggered = false;
      window.removeEventListener('touchmove', handleTouchMove, captureOptions);
      window.removeEventListener('touchend', handleTouchEnd, captureOptions);
      window.removeEventListener('touchcancel', handleTouchEnd, captureOptions);
    };
    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      startInteraction(touch.clientX, touch.clientY);
      window.addEventListener('touchmove', handleTouchMove, passiveCaptureOptions);
      window.addEventListener('touchend', handleTouchEnd, captureOptions);
      window.addEventListener('touchcancel', handleTouchEnd, captureOptions);
    };
    const handleWheel = () => {
      handleUserInteraction();
    };
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.leaflet-control-zoom')) {
        handleUserInteraction();
      }
    };

    container.addEventListener('mousedown', handleMouseDown, captureOptions);
    container.addEventListener('touchstart', handleTouchStart, passiveCaptureOptions);
    container.addEventListener('wheel', handleWheel, passiveCaptureOptions);
    container.addEventListener('click', handleClick, captureOptions);

    return () => {
      handleMouseUp();
      handleTouchEnd();
      container.removeEventListener('mousedown', handleMouseDown, captureOptions);
      container.removeEventListener('touchstart', handleTouchStart, passiveCaptureOptions);
      container.removeEventListener('wheel', handleWheel, passiveCaptureOptions);
      container.removeEventListener('click', handleClick, captureOptions);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const container = mapContainerRef.current;
    if (!map || !container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      map.invalidateSize({ animate: false });
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const markerLayer = markerLayerRef.current;
    if (!map || !markerLayer) return;

    const markers = markersRef.current;

    // Remove markers that are no longer present.
    markers.forEach((marker, id) => {
      if (!pointsById.has(id)) {
        markerLayer.removeLayer(marker);
        markers.delete(id);
      }
    });

    points.forEach((point) => {
      const isSelected = selectedPointId === point.id;
      const icon = createMarkerIcon(point, { selected: isSelected });

      const existing = markers.get(point.id);
      if (existing) {
        existing.setLatLng([point.lat, point.lng]);
        existing.setIcon(icon);
        return;
      }

      const marker = L.marker([point.lat, point.lng], {
        icon,
        keyboard: false,
        title: point.label,
        riseOnHover: true,
      });
      marker.on('click', () => {
        onSelectPointRef.current(point.id);
      });

      marker.addTo(markerLayer);
      markers.set(point.id, marker);
    });

    if (!hasFitBoundsRef.current && points.length > 0) {
      hasFitBoundsRef.current = true;

      if (points.length === 1) {
        const point = points[0];
        map.setView([point.lat, point.lng], FOCUS_ZOOM, { animate: false });
        return;
      }

      const bounds = L.latLngBounds(points.map((point) => [point.lat, point.lng] as const));
      map.fitBounds(bounds, { padding: [24, 24], animate: false });
    }
  }, [points, pointsById, selectedPointId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || selectedPointId === null) return;

    const selected = pointsById.get(selectedPointId);
    if (!selected) return;

    const targetZoom = Math.max(map.getZoom(), FOCUS_ZOOM);
    map.flyTo([selected.lat, selected.lng], targetZoom, { duration: 0.6 });
  }, [pointsById, selectedPointId, focusRequest]);

  return (
    <div className={clsx('sd-availabilities-map h-full w-full', className)}>
      <div
        ref={mapContainerRef}
        className="h-full min-h-[320px] w-full overflow-hidden rounded-3xl border border-surface-subtle shadow-card"
      />
    </div>
  );
}

const HTML_ESCAPE_LOOKUP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function createMarkerIcon(
  point: AvailabilitiesMapPoint,
  options: { selected: boolean },
): L.DivIcon {
  const countLabel = point.unitCount > 99 ? '99+' : point.unitCount.toString();
  const classes = clsx(
    'sd-availabilities-marker',
    options.selected && 'is-selected',
    point.emphasis === true ? 'is-emphasis' : undefined,
  );

  return L.divIcon({
    className: '',
    html: `<div class="${classes}">${escapeHtml(countLabel)}</div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => HTML_ESCAPE_LOOKUP[character] ?? character);
}
